import { GlobalStyles } from "@mui/material";
import type { FC } from "react";
import { memo, useCallback, useMemo, useState } from "react";
import type { Layout, LayoutItem } from "react-grid-layout";
import ReactGridLayout, { useContainerWidth } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import ScrollableFlexContainer from "@entrilia/shared/components/ScrollableFlexContainer";
import { parseJsonObject } from "@entrilia/shared/utilities/objectHelper.ts";
import { WidgetType } from "../../../api/types/dashboardsTypes.ts";
import { defaultFilterWidgetSize, defaultSmartViewWidgetSize } from "./components/widgets-toolbox/generateWidgetId";
import { useDashboardContext } from "./contexts/DashboardContext.tsx";
import { isTileObjectSmartView } from "./models/isTileObjectSmartView.ts";
import type { TileObject } from "./models/TileObject";
import "./styles.css";
import { convertReportToDashboardReport } from "./utils/convertReportToDashboardReport.ts";
import { convertTileLocationToLayout } from "./utils/tileLocationUtils";
import TileComponent from "./widgets/TileComponent";
import TileRenderer from "./widgets/TileRenderer";

const canvasGlobalStyles = (
  <GlobalStyles
    styles={(theme) => ({
      ".edit-layout": {
        backgroundImage: `radial-gradient(${theme.palette.divider} 1px, transparent 0)`,
        backgroundSize: "18px 18px",
      },
      ".react-grid-item > .react-resizable-handle::after": {
        borderRightColor: theme.palette.action.disabled,
        borderBottomColor: theme.palette.action.disabled,
        opacity: 0.4,
      },
    })}
  />
);

interface DashboardLayoutProps {
  className?: string;
  rowHeight?: number;
  cols?: number;
  tiles: TileObject[];
  onNewTile: (tile: TileObject) => void;
  onLayoutChange: (layout: Layout) => void;
  onTileUpdate: (tile: TileObject) => void;
}

const CanvasEdit: FC<DashboardLayoutProps> = ({
  className = "edit-layout",
  rowHeight = 1,
  cols = 24,
  tiles,
  onNewTile,
  onLayoutChange,
  onTileUpdate,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const { setSelectedWidgetId } = useDashboardContext();
  const { width, containerRef, mounted } = useContainerWidth();

  const handleDragStart = useCallback(() => {
    setIsDragging(true);
  }, []);

  const handleDragStop = useCallback(() => {
    setIsDragging(false);
  }, []);

  const layout = useMemo(
    () => tiles?.map((tile) => convertTileLocationToLayout(tile.item.location, tile.id)) ?? [],
    [tiles]
  );

  const generateDOM = useMemo(() => {
    return tiles?.map((tile) => (
      <TileComponent key={tile.id} tile={tile}>
        <TileRenderer tile={tile} />
      </TileComponent>
    ));
  }, [tiles]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    const isSmartView = e.dataTransfer?.types.includes(WidgetType.SmartView.toLowerCase());
    if (isSmartView) {
      return { w: defaultSmartViewWidgetSize.width, h: defaultSmartViewWidgetSize.height };
    }

    const isFilter = e.dataTransfer?.types.includes(WidgetType.Filter.toLowerCase());
    if (isFilter) {
      return { w: defaultFilterWidgetSize.width, h: defaultFilterWidgetSize.height };
    }

    return { w: 6, h: 4 };
  }, []);

  const handleDrop = useCallback(
    (_: Layout, item: LayoutItem | undefined, e: Event) => {
      if (!item) {
        return;
      }

      const data = (e as DragEvent).dataTransfer?.getData("application/json");
      const tile = parseJsonObject<TileObject>(data || "{}");
      if (!tile) {
        return;
      }

      tile.item.location.column = item.x ?? 0;
      tile.item.location.row = item.y ?? 0;
      tile.item.widget.tileId = tile.id;
      tile.isInitialized = true;
      if (tile.item.widget.type === WidgetType.SmartView) {
        tile.isInitialized = false;
      }
      onNewTile?.(tile);
      if (isTileObjectSmartView(tile)) {
        convertReportToDashboardReport(tile, tile.item.widget, onTileUpdate);
      }
    },
    [onNewTile, onTileUpdate]
  );

  return (
    <ScrollableFlexContainer containerRef={containerRef} onClick={() => setSelectedWidgetId(undefined)}>
      {canvasGlobalStyles}
      {mounted && (
        <ReactGridLayout
          width={width}
          style={{
            minHeight: "100%",
            flex: 1,
            display: "flex",
            flexDirection: "column",
          }}
          className={`${className} ${isDragging ? "dragging" : ""}`}
          layout={layout}
          gridConfig={{ cols: cols, rowHeight: rowHeight, margin: [16, 12] }}
          dragConfig={{ enabled: true }}
          dropConfig={{ enabled: true }}
          resizeConfig={{ enabled: true, handles: ["se"] }}
          onDrop={handleDrop}
          onDropDragOver={handleDragOver}
          onLayoutChange={onLayoutChange}
          onDragStart={handleDragStart}
          onDragStop={handleDragStop}
        >
          {generateDOM}
        </ReactGridLayout>
      )}
    </ScrollableFlexContainer>
  );
};

export default memo(CanvasEdit);
